// src/components-ui/export-dialog/ExportDialog.tsx
import React, { useState, useEffect } from 'react';
import ExcelIcon from '../icon/excel/Excelcon';
import PdfIcon from '../icon/pdf/PdfIcon';
import WordIcon from '../icon/word/WordIcon';

import { Dialog, Box, ButtonGroup, Spinner } from '@primer/react';
import { DownloadIcon, FileIcon, XIcon } from '@primer/octicons-react';
import { IExportDialogOptions } from '../../contexts/ExportDialogContext';
import { apiClient } from '../../api/apiClient';
import Button from '../button';

import { NotifyHelper } from '../../helpers/toast';
import { downloadFileFromResponse } from '../../utils/fileDownload';

interface IExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  options: IExportDialogOptions;
}

const ExportDialog: React.FC<IExportDialogProps> = ({ isOpen, onClose, options }) => {
  const {
    title = "Xuất tài liệu",
    endPoint,
    data,
    showPreview = true,
    isLandscape 
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Tạo URL preview từ API endpoint
  useEffect(() => {
    if (!isOpen) return;

    const createPreviewFromApi = async () => {
      setIsLoading(true);
      try {
        debugger;
        if (endPoint) {
          // Fetch file từ API
          const response = await apiClient.download(endPoint.replace('{format}', 'pdf'), data, 'application/pdf');

          if (response.is_success && response.data) {
            const blob = response.data;     
            // Tạo object URL cho preview
            const objectUrl = URL.createObjectURL(blob);
            setPreviewUrl(objectUrl);
          } else {
            NotifyHelper.Warning(response.message ?? "Có lỗi");
            setPreviewUrl('');
          }
        } else {
          setPreviewUrl('');
        }
      } catch (error) {
        setPreviewUrl('');
      } finally {
        setIsLoading(false);
      }
    };

    createPreviewFromApi();

    // Cleanup object URL khi component unmount hoặc view thay đổi
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen]);

  const handleExport = async (type: 'excel' | 'pdf' | 'word') => {
   const res = await apiClient.download(endPoint.replace('{format}', type), data, 'application/pdf');
   if (res.is_success && res.data) {
    downloadFileFromResponse(res.data, res.headers);
   }
  };


  

  const renderPreview = () => {
    if (!showPreview) return null;

    if (isLoading) {
      return (
        <Box
          sx={{
            height: '600px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 1,
            backgroundColor: 'canvas.subtle'
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Spinner size="large" />
            <Box sx={{ mt: 2, color: 'fg.muted' }}>
              Đang tải nội dung...
            </Box>
          </Box>
        </Box>
      );
    }



    if (previewUrl) {
      return (
        <Box sx={{ height: '100%', border: '1px solid', borderColor: 'border.default', borderRadius: 1 }}>
            {/* Làm như thế nào để hiện tên file ở trên cùng của iframe hiện đang gen ký tự ngẫu nhiên ví dụ: e6ee30eb-d0a0-4a6a-a249-10708dac */}
            <iframe
              src={previewUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="PDF Preview"
            />  
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: 1,
          backgroundColor: 'canvas.subtle'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <FileIcon size={48} />
          <Box sx={{ mt: 2, color: 'fg.muted' }}>
            Không có nội dung để xem trước
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      onDismiss={onClose}
      sx={{
        zIndex: 9999,
        width: '90vw',
        maxWidth: isLandscape ? '1600px' : '1400px',
        height: '100vh',
        maxHeight: '100vh',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Dialog.Header sx={{height: '40px'}}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DownloadIcon size={16} />
            {title}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: -200 }}>    
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ color: 'fg.muted', fontSize: 1 }}>
                Chọn định dạng để xuất file
                </Box>
                <Button
                leadingVisual={ExcelIcon}
                onClick={() => handleExport('excel')}
                disabled={isLoading}
                sx={{ minWidth: '100px' }}
                size="medium"
                >
                {isLoading ? <Spinner size="small" /> : 'Excel'}
                </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                leadingVisual={PdfIcon}
                onClick={() => handleExport('pdf')}
                disabled={isLoading}
                sx={{ minWidth: '100px' }}
                >
                {isLoading ? <Spinner size="small" /> : 'PDF'}
                </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                leadingVisual={WordIcon}
                onClick={() => handleExport('word')}
                disabled={isLoading}
                sx={{ minWidth: '100px' }}
                >
                {isLoading ? <Spinner size="small" /> : 'Word'}
                </Button>
             </Box>
            </Box>
            <Button
                variant="invisible"
                onClick={onClose}
                sx={{ p: 1 }}
            >
            </Button>
        </Box>
      </Dialog.Header>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>

        {/* Preview Area */}
        <Box sx={{ flex: 1, p: 2 }}>
          {renderPreview()}
        </Box>

       
      </Box>
    </Dialog>
  );
};

export default ExportDialog;